/**
 * PATH: packages/bills/src/components/BillsListPanel.tsx
 * FILE_ID: BILLS-PKG-CMP-LIST-001
 * PURPOSE: Left rail of the 3-pane workspace — filter + select a bill.
 *
 * WHY: Bookkeepers triage by status (extracted → review → push). They
 *      need a fast keyboard-friendly list with status filter and search,
 *      and the row click must be the *only* selection mechanism.
 *
 * NOTE on the 300-line rule: this file is over the limit because it
 * orchestrates one tightly-coupled UI surface — the panel chrome plus
 * the BillRow / ContextMenu renderers that share its state. Everything
 * that stands alone has been extracted: statement + date grouping in
 * ``billsListGrouping.ts``, monogram tinting in ``avatar-tint.ts``,
 * roving-tabindex math in ``billsListKeyboardNav.ts``, and the
 * pure-presentational leaves (status/due/paid chips, section headers,
 * skeleton / empty / error states, formatters) in
 * ``billsListLeaves.tsx``.
 *
 * A11Y: the scroll region is a ``role="listbox"`` and each bill row a
 * ``role="option"`` with ``aria-selected``. Keyboard nav is roving
 * tabindex — ArrowUp/Down/Home/End move the highlight (skipping group
 * headers) and drive both native focus and the virtualizer's
 * scrollToIndex so it works under windowing; Enter/Space open the row.
 */

import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import {
  AlertCircle,
  Paperclip,
  PencilLine,
  RefreshCw,
  RotateCcw,
  Search,
} from 'lucide-react';
import { Button, FilterChip, Input } from '@quotech/ui';
import { RECLASSIFY_OPTIONS } from '../reclassify';
import { isFailClosedStatus } from '../types';
import type {
  BillFlag,
  BillListRow,
  BillStatus,
  BillStatusFilter,
  BillsListResponse,
} from '../types';
import { BillsQueueHeader } from './BillsQueueHeader';
import { BillsBulkBar } from './BillsBulkBar';
import {
  amountProvenance,
  blockedReason,
  isBlockedStatus,
  type BulkAction,
  type QueueFilter,
} from './billsQueueModel';
import { BillAllocationBadge } from './BillAllocation';
import {
  groupSiblings,
  sumNumeric,
  withDateSections,
  type BillRowVariant,
  type RenderItem,
} from './billsListGrouping';
import { useListVirtualizer, type WindowRowProps } from './virtualized-list';
import {
  nextOptionIndex,
  resolveHighlightIndex,
  toNavKey,
} from './billsListKeyboardNav';
import { avatarInitial, avatarTint } from './avatar-tint';
import {
  BillListSkeleton,
  DateHeader,
  DueDateLabel,
  EmptyState,
  FromStatementPill,
  GroupHeader,
  ListMessage,
  PaidChip,
  StatusPill,
  dueMeta,
  formatCurrency,
  formatCurrencyWhole,
  formatShortDate,
  isOverdue,
} from './billsListLeaves';

// The normal AP pipeline — always shown.
const STATUS_TABS: Array<{ key: BillStatus | 'all'; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'extracted', label: 'Extracted' },
  { key: 'reviewed', label: 'Reviewed' },
  { key: 'pushed', label: 'Pushed' },
  { key: 'failed', label: 'Failed' },
];

// Leak-proof-cascade review queues. Rendered only when they hold rows (or
// are the active filter) so they stay invisible until the cascade actually
// holds something back — no empty clutter in the steady state.
// Quarantined / Awaiting-doc chips are intentionally not surfaced here: those
// fail-closed states still exist on the backend, but operators triage them
// elsewhere, so we keep the rail focused on the actionable fetch/review queues.
const REVIEW_TABS: Array<{ key: BillStatus; label: string; title: string }> = [
  {
    key: 'needs_fetch',
    label: 'Needs fetch',
    // needs_fetch is auto-retried by the backend sweep (capped, backed
    // off) — the operator only steps in for rows the retries can't fix.
    title:
      'Document could not be fetched — retried automatically in the background. Restore manually if it\u2019s a genuine bill the retries can\u2019t recover.',
  },
  {
    key: 'needs_review',
    label: 'Needs review',
    title:
      'Held back by the leak-proof verification cascade — review and restore if it\u2019s a genuine bill.',
  },
];

interface Props {
  data: BillsListResponse | null;
  loading: boolean;
  error: string | null;
  selectedBillId: string | null;
  status: BillStatusFilter;
  onChangeStatus: (s: BillStatusFilter) => void;
  /**
   * Attention-reason filter (queue redesign). When ``onChangeFlag`` is
   * wired, the panel renders the queue header (money line + filter
   * rail). Without it, it falls back to the legacy single chip row —
   * older embedders keep compiling. The list is always newest-first.
   */
  flag?: BillFlag;
  onChangeFlag?: (f: BillFlag | undefined) => void;
  /**
   * Bulk actions (retry read / restore / dismiss). When wired, rows are
   * multi-selectable (checkbox / ``x`` key) and the bulk bar renders at
   * the panel foot with a what-will-change preview before anything runs.
   */
  onBulkAction?: (action: BulkAction, rows: BillListRow[]) => Promise<void> | void;
  bulkBusy?: boolean;
  /** Retry the machine read on one blocked row (email-sourced only). */
  onRetryRead?: (row: BillListRow) => Promise<void> | void;
  onSelect: (billId: string) => void;
  onRefresh: () => void;
  /**
   * Right-click → "Not a bill, this is..." → category.
   * Optional so older embedders that haven't wired the flow up still
   * compile; when absent, right-click falls back to the browser default
   * context menu (which is what we did before).
   *
   * Called with the email_id (NOT the bill_id) because the backend's
   * bulk set_category endpoint is keyed on the email row.
   */
  onReclassify?: (emailId: string, category: string) => Promise<void> | void;
  /**
   * Restore a cascade fail-closed bill back to the queue. Optional — when
   * absent (e.g. apps/billing's client hasn't wired `restore`), the
   * per-row Restore affordance is hidden and the review tabs are still
   * browsable (read-only).
   */
  onRestore?: (billId: string) => Promise<void> | void;
  /**
   * Controlled search value + change handler. When BOTH are provided the
   * search box is server-side (the parent passes ``q`` to the list hook and
   * the backend filters). When absent the panel falls back to LOCAL
   * client-side filtering of ``data.bills`` — preserving the pre-P1-2b
   * behaviour for any embedder that hasn't wired server search.
   */
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  /**
   * Infinite scroll. ``onLoadMore`` is called when the list is scrolled near
   * the bottom and ``hasMore`` is true; ``loadingMore`` renders a footer
   * spinner. All optional — without them the list is a single page (current
   * behaviour).
   */
  onLoadMore?: () => void;
  hasMore?: boolean;
  loadingMore?: boolean;
}

interface ContextMenuState {
  row: BillListRow;
  x: number;
  y: number;
}

export function BillsListPanel({
  data,
  loading,
  error,
  selectedBillId,
  status,
  onChangeStatus,
  flag,
  onChangeFlag,
  onBulkAction,
  bulkBusy,
  onRetryRead,
  onSelect,
  onRefresh,
  onReclassify,
  onRestore,
  searchValue,
  onSearchChange,
  onLoadMore,
  hasMore,
  loadingMore,
}: Props) {
  // The queue header renders only when the parent wires the queue-filter
  // surface; otherwise the legacy chip row keeps working.
  const queueMode = onChangeFlag !== undefined;
  const queueFilter: QueueFilter = { status, flag };
  const applyQueueFilter = useCallback(
    (next: QueueFilter) => {
      onChangeFlag?.(next.flag);
      onChangeStatus(next.status);
    },
    [onChangeFlag, onChangeStatus],
  );
  // Server-controlled when the parent wires onSearchChange; else local
  // client-side filtering (back-compat). Two branches, one `search` value.
  const controlledSearch = onSearchChange !== undefined;
  const [localSearch, setLocalSearch] = useState('');
  const search = controlledSearch ? searchValue ?? '' : localSearch;
  const setSearch = controlledSearch
    ? (onSearchChange as (v: string) => void)
    : setLocalSearch;
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Multi-select for bulk actions. Cleared whenever the filter changes so
  // an operator never carries an invisible selection into a different view.
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  useEffect(() => {
    setSelectedIds(new Set());
  }, [status, flag]);
  const toggleSelected = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Keyboard highlight (roving tabindex) — the render-item index of the row
  // the arrow keys last moved to. Null until the user navigates; the tab stop
  // then falls back to the selected row, else the first option (see
  // resolveHighlightIndex). ``focusPendingRef`` defers the imperative
  // ``.focus()`` to an effect AFTER the (possibly windowed) row has mounted.
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const focusPendingRef = useRef(false);

  // Close the context menu on click-outside, Escape, scroll, or
  // resize. The latter two prevent a stale anchored menu from
  // floating somewhere unrelated when the layout shifts. Bound only
  // while the menu is open so we don't pay for global listeners.
  useEffect(() => {
    if (!contextMenu) return;
    function close() {
      setContextMenu(null);
    }
    function onPointerDown(e: MouseEvent) {
      if (!contextMenuRef.current) return;
      if (!contextMenuRef.current.contains(e.target as Node)) close();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close();
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [contextMenu]);

  const rows = useMemo(() => {
    const list = data?.bills ?? [];
    // Server-side search: the backend already filtered — don't double-filter
    // (that would hide rows the server matched on fields we don't check here,
    // e.g. reference / sender).
    if (controlledSearch) return list;
    if (!search.trim()) return list;
    const q = search.trim().toLowerCase();
    return list.filter((b) =>
      [b.vendor_name, b.invoice_number, b.email_subject]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [data, search, controlledSearch]);

  // Infinite scroll: when scrolled within ~240px of the bottom and the
  // backend reports another page, ask the parent to append it. Guarded so we
  // don't spam requests while one is in flight or when there's nothing more.
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !onLoadMore || !hasMore || loadingMore) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 240) {
      onLoadMore();
    }
  }, [onLoadMore, hasMore, loadingMore]);

  // Re-shape the flat row list into a render-item stream: statement
  // children grouped under "From statement…" headers (groupSiblings),
  // then date-bucket section labels — Today / Yesterday / This week /
  // Earlier — inserted between buckets (withDateSections). Both passes
  // are pure I→O so they're testable on their own. The list is always
  // newest-first (received order), so date sections always apply.
  const renderItems = useMemo(
    () => withDateSections(groupSiblings(rows)),
    [rows],
  );

  const selectedRows = useMemo(
    () => rows.filter((r) => selectedIds.has(r.id)),
    [rows, selectedIds],
  );

  const totalCount = data?.counts?.total ?? data?.bills.length ?? 0;

  // Roll-ups for the header insight strip, computed off the LOADED rows
  // (what the user is looking at) — not the server totals, which the
  // count chips already carry.
  const insight = useMemo(() => {
    const overdueRows = rows.filter((r) => isOverdue(r));
    return {
      count: rows.length,
      total: sumNumeric(rows.map((r) => r.total)),
      overdueCount: overdueRows.length,
      overdueTotal: sumNumeric(overdueRows.map((r) => r.total)),
      currency: rows[0]?.currency || 'AUD',
    };
  }, [rows]);

  // Variable-height virtualization. Headers are a single dense line; rows are
  // three lines (+ an optional restore strip). Estimates only seed the initial
  // layout — real heights are measured per-row after mount.
  const estimateItemSize = useCallback(
    (index: number) => {
      const kind = renderItems[index]?.kind;
      if (kind === 'header') return 30;
      if (kind === 'date-header') return 26;
      return 96;
    },
    [renderItems],
  );

  // Keep the selected bill scrollable-into-view on the windowed path (its DOM
  // node may not be mounted when it's outside the current window).
  const selectedIndex = useMemo(() => {
    if (!selectedBillId) return null;
    return renderItems.findIndex(
      (it) => it.kind === 'row' && it.row.id === selectedBillId,
    );
  }, [renderItems, selectedBillId]);

  // Render-item indices that are selectable options (bill rows). Group headers
  // are NOT options — they're skipped by the roving-tabindex navigation.
  const optionIndices = useMemo(
    () =>
      renderItems.reduce<number[]>((acc, it, i) => {
        if (it.kind === 'row') acc.push(i);
        return acc;
      }, []),
    [renderItems],
  );

  // The render-item index the roving tabindex lives on: the keyboard highlight
  // if the user has navigated, else the selected row, else the first option.
  const highlightIndex = resolveHighlightIndex(
    activeIndex,
    selectedIndex != null && selectedIndex >= 0 ? selectedIndex : null,
    optionIndices,
  );

  // Keep BOTH the keyboard highlight and the selected row scrollable-into-view
  // on the windowed path (their DOM nodes may be unmounted outside the window).
  // The highlight takes precedence while navigating so ArrowDown never scrolls
  // to a stale selection.
  const virtualActiveIndex =
    activeIndex ?? (selectedIndex != null && selectedIndex >= 0 ? selectedIndex : null);

  const listWindow = useListVirtualizer({
    count: renderItems.length,
    scrollRef,
    estimateSize: estimateItemSize,
    activeIndex: virtualActiveIndex,
  });

  // Roving-tabindex keyboard navigation. Bound to the listbox container so it
  // catches keydowns bubbling from the focused option button. Arrow/Home/End
  // move the highlight (pure math in ``nextOptionIndex``, headers skipped) and
  // flag a deferred focus; Enter/Space open the highlighted row — we
  // ``preventDefault`` so the button's native activation doesn't double-fire
  // onSelect (and so the container doesn't scroll on Space).
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (optionIndices.length === 0) return;
      const navKey = toNavKey(e.key);
      if (navKey) {
        e.preventDefault();
        const next = nextOptionIndex(optionIndices, highlightIndex, navKey);
        if (next != null) {
          setActiveIndex(next);
          focusPendingRef.current = true;
        }
        return;
      }
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        if (highlightIndex == null) return;
        const item = renderItems[highlightIndex];
        if (item?.kind === 'row') {
          e.preventDefault();
          onSelect(item.row.id);
        }
        return;
      }
      // Queue keyboard (design §3.4): x toggles selection on the
      // highlighted row, Esc clears the selection, / jumps to search.
      if (e.key === 'x' && onBulkAction) {
        if (highlightIndex == null) return;
        const item = renderItems[highlightIndex];
        if (item?.kind === 'row') {
          e.preventDefault();
          toggleSelected(item.row.id);
        }
        return;
      }
      if (e.key === 'Escape' && onBulkAction) {
        setSelectedIds(new Set());
        return;
      }
      if (e.key === '/') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    },
    [optionIndices, highlightIndex, renderItems, onSelect, onBulkAction, toggleSelected],
  );

  // Move real DOM focus to the highlighted option after it renders (so it works
  // on the windowed path once scrollToIndex has mounted the row). Guarded by
  // ``focusPendingRef`` so we ONLY steal focus in response to a keyboard nav —
  // never on selection, data refreshes, or when the context menu closes (which
  // would otherwise trap focus back into the list).
  useEffect(() => {
    if (!focusPendingRef.current) return;
    focusPendingRef.current = false;
    if (highlightIndex == null) return;
    const el = scrollRef.current?.querySelector<HTMLElement>(
      `[data-option-index="${highlightIndex}"]`,
    );
    el?.focus();
  }, [highlightIndex, listWindow.items]);

  // One render mapping shared by both paths so the plain and windowed lists
  // stay byte-identical in what they render. ``windowProps`` is null on the
  // plain path and the measure/position props on the windowed path.
  const renderRenderItem = (
    item: RenderItem,
    renderIndex: number,
    windowProps: WindowRowProps | null,
  ) => {
    if (item.kind === 'date-header') {
      return (
        <DateHeader key={item.key} label={item.label} windowProps={windowProps} />
      );
    }
    if (item.kind === 'header') {
      return <GroupHeader key={item.key} header={item} windowProps={windowProps} />;
    }
    const row = item.row;
    return (
      <BillRow
        key={row.id}
        row={row}
        active={row.id === selectedBillId}
        variant={item.variant}
        statusFilter={status}
        optionIndex={renderIndex}
        tabIndex={renderIndex === highlightIndex ? 0 : -1}
        onSelect={() => onSelect(row.id)}
        onRestore={onRestore}
        onRetryRead={onRetryRead}
        checked={onBulkAction ? selectedIds.has(row.id) : undefined}
        onToggleChecked={onBulkAction ? () => toggleSelected(row.id) : undefined}
        windowProps={windowProps}
        onContextMenu={
          // Reclassify only makes sense for email-sourced rows — manual bills
          // have no email behind them and the menu would be a no-op. Skip
          // wiring the right-click handler for those rows so the browser's
          // default context menu re-appears (and operators can still
          // copy/paste text from the row).
          onReclassify && row.email_id
            ? (e) => {
                e.preventDefault();
                // Anchor at the cursor; the menu uses fixed positioning so
                // this works even inside the scrollable list. We close on
                // scroll so a stale anchor never lingers.
                setContextMenu({ row, x: e.clientX, y: e.clientY });
              }
            : undefined
        }
      />
    );
  };

  return (
    <aside className="flex h-full min-h-0 w-full flex-col border-r border-border bg-card">
      <header className="shrink-0 space-y-3 border-b border-border bg-card p-4">
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-baseline gap-2">
            <h2 className="text-base font-semibold tracking-tight text-foreground">
              Bills
            </h2>
            <span className="rounded-md bg-muted px-1.5 py-0.5 text-xs font-medium tabular-nums text-muted-foreground">
              {totalCount}
            </span>
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={onRefresh}
            aria-label="Refresh bills"
            title="Refresh"
            className="h-8 w-8 rounded-lg p-0 text-muted-foreground hover:text-foreground"
          >
            <RefreshCw className="h-4 w-4" aria-hidden />
          </Button>
        </div>
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            ref={searchInputRef}
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search vendor, invoice, reference"
            aria-label="Search bills"
            className="h-10 rounded-lg pl-9"
          />
        </div>
        {queueMode ? (
          // Queue header: server-computed money line (known / no-amount /
          // overdue) + the filter rail (All / Extracted / Manual review /
          // Couldn't fetch). Replaces BOTH the legacy chip row and the
          // page-local insight strip below.
          <BillsQueueHeader
            data={data}
            filter={queueFilter}
            onChangeFilter={applyQueueFilter}
          />
        ) : (
          <>
        <div
          role="group"
          aria-label="Filter bills by status"
          className="flex flex-wrap gap-1.5"
        >
          {STATUS_TABS.map((t) => {
            const count =
              t.key === 'all' ? totalCount : data?.counts?.by_status?.[t.key] ?? 0;
            const active = status === t.key;
            return (
              <FilterChip
                key={t.key}
                active={active}
                count={count}
                onClick={() => onChangeStatus(t.key)}
              >
                {t.label}
              </FilterChip>
            );
          })}
          {REVIEW_TABS.map((t) => {
            const count = data?.counts?.by_status?.[t.key] ?? 0;
            const active = status === t.key;
            // Keep the rail clean: only surface a review queue once it has
            // something in it (or it's the tab the user is currently on).
            if (count === 0 && !active) return null;
            return (
              <FilterChip
                key={t.key}
                tone="danger"
                active={active}
                count={count}
                onClick={() => onChangeStatus(t.key)}
                title={t.title}
              >
                {t.label}
              </FilterChip>
            );
          })}
        </div>
        {insight.count > 0 && (
          <div className="flex gap-1.5 text-[11px]">
            <span className="flex min-w-0 items-center gap-1 rounded-lg bg-muted/60 px-2 py-1 text-muted-foreground">
              <span className="truncate">
                <span className="font-semibold tabular-nums text-foreground">
                  {formatCurrencyWhole(insight.total, insight.currency)}
                  {hasMore ? '+' : ''}
                </span>{' '}
                in queue · {insight.count} {insight.count === 1 ? 'bill' : 'bills'}
              </span>
            </span>
            {insight.overdueCount > 0 && (
              <span className="flex min-w-0 shrink-0 items-center gap-1 rounded-lg bg-rose-50 px-2 py-1 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300">
                <AlertCircle className="h-3 w-3 shrink-0" aria-hidden />
                <span className="truncate">
                  <span className="font-semibold tabular-nums">
                    {formatCurrencyWhole(insight.overdueTotal, insight.currency)}
                  </span>{' '}
                  overdue · {insight.overdueCount}
                </span>
              </span>
            )}
          </div>
        )}
          </>
        )}
      </header>

      <div
        ref={scrollRef}
        onScroll={handleScroll}
        onKeyDown={handleKeyDown}
        role="listbox"
        aria-label="Bills"
        aria-busy={loading && !data ? true : undefined}
        className="min-h-0 flex-1 overflow-y-auto p-2 focus:outline-none"
      >
        {loading && !data ? (
          <BillListSkeleton />
        ) : error ? (
          <ListMessage tone="error" onRetry={onRefresh}>{error}</ListMessage>
        ) : renderItems.length === 0 ? (
          <EmptyState />
        ) : listWindow.enabled ? (
          // Windowed path (large lists): a sized container holds absolutely
          // positioned rows; only the visible window (+ overscan) is mounted.
          // Each row carries its own card border + inter-row gap (vertical
          // padding on the <li>) so no shared divider is needed under
          // windowing. ``role="presentation"`` flattens the <ul> so the
          // listbox → option relationship isn't interrupted by a list role.
          <ul
            role="presentation"
            className="relative"
            style={{ height: listWindow.totalSize }}
          >
            {listWindow.items.map((vi) =>
              renderRenderItem(
                renderItems[vi.index],
                vi.index,
                listWindow.rowProps(vi.index, vi.start),
              ),
            )}
          </ul>
        ) : (
          // Plain path (small lists): every row in the DOM, exactly as before.
          <ul role="presentation">
            {renderItems.map((item, i) => renderRenderItem(item, i, null))}
          </ul>
        )}
        {loadingMore && (
          <div className="p-3 text-center text-xs text-muted-foreground">
            Loading more…
          </div>
        )}
      </div>

      {onBulkAction && (
        <BillsBulkBar
          selectedRows={selectedRows}
          busy={!!bulkBusy}
          onClear={() => setSelectedIds(new Set())}
          onRun={onBulkAction}
        />
      )}
      {onBulkAction && selectedRows.length === 0 && (
        <div className="shrink-0 border-t border-border/60 px-3 py-1 text-[10px] text-muted-foreground">
          ↑↓ move · Enter open · x select · / search
        </div>
      )}

      {contextMenu && onReclassify && (
        <ContextMenu
          ref={contextMenuRef}
          state={contextMenu}
          onPick={async (category) => {
            const target = contextMenu;
            setContextMenu(null);
            // Manual bills (source='manual') have email_id=null —
            // there's no underlying email to set a category on, so
            // the reclassify is a no-op. The context menu shouldn't
            // even open for those rows in practice (see onContextMenu
            // above) but guard here defensively against a future
            // caller wiring it up differently.
            if (!target.row.email_id) return;
            await onReclassify(target.row.email_id, category);
          }}
          onClose={() => setContextMenu(null)}
        />
      )}
    </aside>
  );
}

function BillRow({
  row,
  active,
  variant,
  statusFilter,
  optionIndex,
  tabIndex,
  onSelect,
  onContextMenu,
  onRestore,
  onRetryRead,
  checked,
  onToggleChecked,
  windowProps,
}: {
  row: BillListRow;
  active: boolean;
  variant: BillRowVariant;
  /** The active status tab — a row's status pill is suppressed when it
   *  merely repeats the filter (every row would say the same thing). */
  statusFilter: BillStatusFilter;
  /** Render-item index — used as the roving-tabindex focus target key. */
  optionIndex: number;
  /** 0 for the current roving tab stop, -1 otherwise. */
  tabIndex: number;
  onSelect: () => void;
  onContextMenu?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onRestore?: (billId: string) => Promise<void> | void;
  onRetryRead?: (row: BillListRow) => Promise<void> | void;
  /** Multi-select state; undefined = selection not wired (no checkbox). */
  checked?: boolean;
  onToggleChecked?: () => void;
  /** When set (windowed path), positions + measures this row. Null otherwise. */
  windowProps?: WindowRowProps | null;
}) {
  const isGroupedChild = variant === 'grouped-child';
  const canRestore = !!onRestore && isFailClosedStatus(row.status);
  const provenance = amountProvenance(row);
  const stuckReason = isBlockedStatus(row.status) ? blockedReason(row) : null;
  const canRetryRead =
    !!onRetryRead &&
    row.status === 'failed' &&
    !row.reviewed_at &&
    !!row.email_id;
  const vendor = row.vendor_name || '— Unknown vendor —';
  const reference = row.invoice_number || row.email_subject || 'No reference';
  const dateLabel = row.email_received_at ? formatShortDate(row.email_received_at) : '';
  const tint = avatarTint(row.vendor_name);
  const initial = avatarInitial(row.vendor_name);
  const due = dueMeta(row);
  const paid = row.payment_status === 'paid';
  // Zone 3 chips render only when at least one is meaningful, so quiet rows
  // stay two lines and don't pay an empty-row gap.
  const showStatus = statusFilter === 'all' || row.status !== statusFilter;
  const hasAllocation =
    row.allocation_status === 'linked' ||
    row.allocation_status === 'suggested' ||
    row.allocation_status === 'needs_review';
  const isDuplicate = (row.dup_count ?? 0) > 1;
  const hasChips = showStatus || hasAllocation || paid || isDuplicate;
  // Accent bar on the card's left edge — a complete status channel, not
  // just an alarm. Every row gets a notch so calm rows read as deliberately
  // calm instead of unstyled: rose = overdue, amber = due soon, emerald =
  // paid (settled), neutral = on track / nothing urgent. Selection wins over
  // everything so the selected row always reads as selected.
  const accent = active
    ? 'border-l-primary'
    : paid
      ? 'border-l-emerald-400'
      : due?.tone === 'overdue'
        ? 'border-l-rose-500'
        : due?.tone === 'soon'
          ? 'border-l-amber-400'
          : 'border-l-border';
  return (
    <li
      role="presentation"
      ref={windowProps?.ref}
      data-index={windowProps?.['data-index']}
      style={windowProps?.style}
      className={
        'flex items-start gap-1.5 px-1 py-0.5 ' + (isGroupedChild ? 'pl-5' : '')
      }
    >
      {onToggleChecked && (
        <input
          type="checkbox"
          checked={!!checked}
          onChange={onToggleChecked}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Select ${vendor}${row.invoice_number ? ` ${row.invoice_number}` : ''}`}
          className="mt-4 h-3.5 w-3.5 shrink-0 cursor-pointer rounded border-border accent-primary"
        />
      )}
      <div className="min-w-0 flex-1">
      <button
        type="button"
        role="option"
        aria-selected={active}
        data-option-index={optionIndex}
        tabIndex={tabIndex}
        onClick={onSelect}
        onContextMenu={onContextMenu}
        className={
          'group flex w-full items-center gap-3 rounded-xl border border-l-[3px] px-3 py-2.5 text-left text-sm outline-none transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring ' +
          accent +
          ' ' +
          (active
            ? // Selected: soft primary tint + subtle ring. Kept distinct from
              // the hover ``bg-muted/50`` so a hovered row never reads as
              // selected.
              'border-primary/20 bg-primary/10 ring-1 ring-inset ring-primary/20'
            : isGroupedChild
              ? 'border-border/60 bg-violet-50/40 hover:bg-muted/50 dark:bg-violet-950/10'
              : 'border-border/60 hover:bg-muted/50')
        }
      >
        {/* Monogram avatar — deterministic soft tint per vendor. */}
        <span
          aria-hidden
          className={
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-xl text-sm font-medium ' +
            tint
          }
        >
          {initial}
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          {/* Zone 1: vendor (full width, tooltip carries the untruncated
              name + email) + amount — nothing else competes for space. */}
          <span className="flex items-baseline justify-between gap-2">
            <span className="flex min-w-0 items-center gap-1.5">
              <span
                className="truncate font-medium text-foreground"
                title={row.vendor_email ? `${vendor} · ${row.vendor_email}` : vendor}
              >
                {vendor}
              </span>
              {variant === 'orphan-child' && <FromStatementPill />}
            </span>
            {/* Amount + provenance mark: a human-verified figure must not
                look identical to a low-confidence machine guess. ✎ = a
                person keyed/approved it; ~ = machine-read at low
                confidence; unmarked = machine-read, unreviewed. "—" for
                no amount, with the reason in the tooltip. */}
            <span
              className="flex shrink-0 items-baseline gap-1"
              title={provenance.label}
            >
              {provenance.kind === 'human' && (
                <PencilLine
                  className="h-3 w-3 self-center text-emerald-600 dark:text-emerald-400"
                  aria-label="Verified by a person"
                />
              )}
              {provenance.kind === 'ai' && provenance.low && (
                <span
                  className="self-center text-[11px] font-bold text-amber-600 dark:text-amber-400"
                  aria-label="Low-confidence machine read"
                >
                  ~
                </span>
              )}
              <span
                className={
                  'text-[15px] font-semibold tabular-nums ' +
                  (provenance.kind === 'unknown'
                    ? 'text-muted-foreground'
                    : 'text-foreground')
                }
              >
                {formatCurrency(row.total, row.currency)}
              </span>
            </span>
          </span>
          {/* Zone 2: reference + received date (+ attachment cue) on the
              left; the due date on the right — always occupied, either by
              the real due state or a "No due date" placeholder. */}
          <span className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
            <span className="flex min-w-0 items-center gap-1">
              <span className="truncate" title={reference}>
                {reference}
                {dateLabel && <span className="tabular-nums"> · {dateLabel}</span>}
              </span>
              {row.has_attachments && (
                <Paperclip
                  className="h-3 w-3 shrink-0"
                  aria-label="Has attachment"
                />
              )}
            </span>
            {due ? (
              <DueDateLabel due={due} />
            ) : (
              // Explicit placeholder so a missing due date reads as a known
              // data gap, not an empty/broken row.
              <span className="shrink-0 text-xs italic text-muted-foreground/70">
                No due date
              </span>
            )}
          </span>
          {/* Zone 3: workflow chips — status (unless it just repeats the
              active filter), job allocation, paid. Rendered only when at
              least one chip has something to say. */}
          {hasChips && (
            <span className="flex flex-wrap items-center gap-1.5 pt-0.5">
              {showStatus && <StatusPill status={row.status} />}
              <BillAllocationBadge row={row} />
              {paid && <PaidChip />}
              {isDuplicate && (
                <span
                  className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-500/15 dark:text-amber-300"
                  title={`${row.dup_count} bills share this vendor + invoice number. Keep one, dismiss the rest.`}
                >
                  <AlertCircle className="h-2.5 w-2.5" aria-hidden />
                  Duplicate? ×{row.dup_count}
                </span>
              )}
            </span>
          )}
        </span>
      </button>
      {stuckReason && (
        // Why this row is stuck + the one action that unsticks it — in
        // operator language, derived from row data alone (billsQueueModel).
        <div className="mt-1 flex items-center justify-between gap-2 rounded-lg bg-rose-50/70 px-3 py-1.5 text-xs text-rose-700 dark:bg-rose-950/20 dark:text-rose-300">
          <span className="truncate" title={stuckReason}>
            {stuckReason}
          </span>
          {canRestore && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onRestore?.(row.id)}
              className="h-6 shrink-0 gap-1 border border-rose-300 px-2 text-xs font-medium text-rose-700 hover:bg-rose-100 dark:border-rose-800 dark:text-rose-300 dark:hover:bg-rose-900/40"
            >
              <RotateCcw className="h-3 w-3" aria-hidden /> Restore
            </Button>
          )}
          {canRetryRead && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onRetryRead?.(row)}
              className="h-6 shrink-0 gap-1 border border-rose-300 px-2 text-xs font-medium text-rose-700 hover:bg-rose-100 dark:border-rose-800 dark:text-rose-300 dark:hover:bg-rose-900/40"
            >
              <RotateCcw className="h-3 w-3" aria-hidden /> Retry read
            </Button>
          )}
        </div>
      )}
      </div>
    </li>
  );
}

/**
 * Anchored "Not a bill?" context menu rendered on right-click of a row.
 *
 * Positioning notes:
 *   - We use `position: fixed` keyed off `clientX/Y` so the menu is
 *     unaffected by the surrounding scroll container.
 *   - We clamp to the viewport on the next frame: if the anchored
 *     position would push the menu off-screen, we shift it back. This
 *     is read off the actual menu width / height post-mount so we
 *     don't have to hard-code dimensions.
 *
 * Anti-noise:
 *   - The parent `BillsListPanel` closes the menu on click-outside,
 *     Escape, scroll, and resize — so this component just renders.
 */
const ContextMenu = forwardRef<
  HTMLDivElement,
  {
    state: ContextMenuState;
    onPick: (category: string) => void;
    onClose: () => void;
  }
>(function ContextMenu({ state, onPick, onClose }, ref) {
  const [style, setStyle] = useState<CSSProperties>({
    position: 'fixed',
    top: state.y,
    left: state.x,
    visibility: 'hidden',
  });
  const innerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = innerRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const margin = 8;
    let left = state.x;
    let top = state.y;
    if (left + rect.width + margin > window.innerWidth) {
      left = Math.max(margin, window.innerWidth - rect.width - margin);
    }
    if (top + rect.height + margin > window.innerHeight) {
      top = Math.max(margin, window.innerHeight - rect.height - margin);
    }
    setStyle({ position: 'fixed', top, left });
  }, [state.x, state.y]);

  return (
    <div
      ref={(node) => {
        innerRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      }}
      role="menu"
      style={style}
      className="z-40 w-72 overflow-hidden rounded-md border border-border bg-popover shadow-lg"
    >
      <div className="border-b border-border px-3 py-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        Not a bill — reclassify as…
      </div>
      <ul className="max-h-80 overflow-y-auto py-1 text-xs">
        {RECLASSIFY_OPTIONS.map((opt) => (
          <li key={opt.value}>
            <button
              type="button"
              role="menuitem"
              onClick={() => onPick(opt.value)}
              className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left hover:bg-accent hover:text-accent-foreground"
            >
              <span className="font-medium">{opt.label}</span>
              <span className="text-[11px] text-muted-foreground">{opt.hint}</span>
            </button>
          </li>
        ))}
      </ul>
      <div className="border-t border-border px-3 py-1.5 text-[10px] text-muted-foreground">
        Locks the email category — the AI won't move it back.
        <button
          type="button"
          onClick={onClose}
          className="ml-2 underline-offset-2 hover:underline"
        >
          Cancel
        </button>
      </div>
    </div>
  );
});

